import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Clock, Trash2 } from 'lucide-react';

import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command';
import { usePermissions } from '@/hooks/use-permissions';

import { useCommandCenterStore } from '../stores/command-center.store';
import { useCommandSearch } from '../hooks/use-command-search';
import { useRecentItems } from '../hooks/use-recent-items';
import { matchStaticCommands, STATIC_COMMANDS } from '../static-commands';
import { SEARCH_MIN_CHARS } from '../constants';
import type { IRecentItem, ISearchResultItem, SearchEntityType } from '../types';

/** Section labels: i18n key + English fallback per entity type. */
const SECTION_LABELS: Record<SearchEntityType, [string, string]> = {
    contact: ['commandCenter.sections.contacts', 'Contacts'],
    campaign: ['commandCenter.sections.campaigns', 'Campaigns'],
    segment: ['commandCenter.sections.segments', 'Segments'],
    tag: ['commandCenter.sections.tags', 'Tags'],
    smsTemplate: ['commandCenter.sections.smsTemplates', 'SMS Templates'],
    emailTemplate: ['commandCenter.sections.emailTemplates', 'Email Templates'],
    whatsappTemplate: ['commandCenter.sections.whatsappTemplates', 'WhatsApp Templates'],
    joinKeyword: ['commandCenter.sections.joinKeywords', 'Keywords'],
    joinLink: ['commandCenter.sections.joinLinks', 'Join Links'],
    shortLink: ['commandCenter.sections.shortLinks', 'Short Links'],
    leadForm: ['commandCenter.sections.leadForms', 'Lead Forms'],
    poll: ['commandCenter.sections.polls', 'Polls'],
    workflow: ['commandCenter.sections.workflows', 'Workflows'],
    aiEmployee: ['commandCenter.sections.aiEmployees', 'AI Employees'],
    meetingType: ['commandCenter.sections.meetingTypes', 'Meeting Types'],
    dashboard: ['commandCenter.sections.dashboards', 'Dashboards'],
    ticket: ['commandCenter.sections.tickets', 'Tickets'],
    member: ['commandCenter.sections.members', 'Team Members'],
};

interface CommandCenterProps {
    /** Org id for recents isolation; empty string falls back to a shared key. */
    orgId: string;
}

export function CommandCenter({ orgId }: CommandCenterProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isOpen = useCommandCenterStore((s) => s.isOpen);
    const close = useCommandCenterStore((s) => s.close);
    const open = useCommandCenterStore((s) => s.open);

    const [query, setQuery] = useState('');
    const search = useCommandSearch(query);
    const { recents, addRecent, removeRecent, clearRecents } = useRecentItems(orgId || 'default');

    // Every close path resets the query so reopening starts fresh.
    const closeAndReset = useCallback(() => {
        close();
        setQuery('');
    }, [close]);

    // Global ⌘K / Ctrl+K.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            // `key` is absent on the synthetic keydowns browser autofill and
            // password managers dispatch. This listener is on `window`, so one
            // of those anywhere in the app threw and broke the page.
            if (event.key?.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                if (isOpen) {
                    closeAndReset();
                } else {
                    open();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, open, closeAndReset]);

    const go = useCallback(
        (recent: IRecentItem) => {
            addRecent(recent);
            closeAndReset();
            navigate(recent.url);
        },
        [addRecent, closeAndReset, navigate],
    );

    const openResult = useCallback(
        (item: ISearchResultItem, url?: string) => {
            go({
                id: `${item.entityType}:${item.id}`,
                entityType: item.entityType,
                title: item.title,
                subtitle: item.subtitle,
                url: url ?? item.url,
            });
        },
        [go],
    );

    const staticMatches = matchStaticCommands(STATIC_COMMANDS, query, hasPermission);
    const showRecents = query.trim().length === 0 && recents.length > 0;
    const isSearching = query.trim().length >= SEARCH_MIN_CHARS;
    const sections = (isSearching && search.data?.sections) || [];
    const nothingFound =
        isSearching && !search.isFetching && sections.length === 0 && staticMatches.length === 0;

    return (
        <CommandDialog
            open={isOpen}
            onOpenChange={(nextOpen: boolean) => {
                if (!nextOpen) closeAndReset();
            }}
            title={t('commandCenter.title', 'Command Center')}
        >
            <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder={t('commandCenter.placeholder', 'Search or jump to…')}
            />
            <CommandList>
                {nothingFound ? (
                    <CommandEmpty>{t('commandCenter.empty', 'No results found.')}</CommandEmpty>
                ) : null}

                {showRecents ? (
                    <CommandGroup heading={t('commandCenter.recent', 'Recent')}>
                        {recents.map((recent) => (
                            <CommandItem
                                key={recent.id}
                                value={recent.id}
                                onSelect={() => go(recent)}
                            >
                                <Clock />
                                <span className="truncate">{recent.title}</span>
                                {recent.subtitle ? (
                                    <span className="text-muted-foreground truncate text-xs">
                                        {recent.subtitle}
                                    </span>
                                ) : null}
                                <button
                                    type="button"
                                    aria-label={t('commandCenter.removeRecent', 'Remove')}
                                    className="ml-auto opacity-50 hover:opacity-100"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeRecent(recent.id);
                                    }}
                                >
                                    <Trash2 className="size-3.5" />
                                </button>
                            </CommandItem>
                        ))}
                        <CommandItem value="__clear-recents" onSelect={clearRecents}>
                            <span className="text-muted-foreground text-xs">
                                {t('commandCenter.clearRecents', 'Clear recent searches')}
                            </span>
                        </CommandItem>
                    </CommandGroup>
                ) : null}

                {staticMatches.length > 0 ? (
                    <CommandGroup
                        heading={
                            query.trim().length === 0
                                ? t('commandCenter.suggested', 'Suggested')
                                : t('commandCenter.commandsHeading', 'Commands')
                        }
                    >
                        {staticMatches.map((cmd) => (
                            <CommandItem
                                key={cmd.id}
                                value={cmd.id}
                                onSelect={() =>
                                    go({
                                        id: cmd.id,
                                        entityType: 'command',
                                        title: t(cmd.i18nKey, cmd.fallback),
                                        url: cmd.url,
                                    })
                                }
                            >
                                <cmd.icon />
                                <span>{t(cmd.i18nKey, cmd.fallback)}</span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                ) : null}

                {sections.length > 0 && staticMatches.length > 0 ? <CommandSeparator /> : null}

                {sections.map(({ entityType, items }) => {
                    const [labelKey, labelFallback] = SECTION_LABELS[entityType] ?? [
                        `commandCenter.sections.${entityType}`,
                        entityType,
                    ];
                    return (
                        <CommandGroup key={entityType} heading={t(labelKey, labelFallback)}>
                            {items.map((item) => (
                                <CommandItem
                                    key={`${entityType}:${item.id}`}
                                    value={`${entityType}:${item.id}`}
                                    onSelect={() => openResult(item)}
                                    onKeyDown={(e) => {
                                        // ⌘Enter triggers the first quick action.
                                        if (
                                            e.key === 'Enter' &&
                                            (e.metaKey || e.ctrlKey) &&
                                            item.actions?.[0]
                                        ) {
                                            e.preventDefault();
                                            openResult(item, item.actions[0].url);
                                        }
                                    }}
                                >
                                    <ArrowRight />
                                    <span className="truncate">{item.title}</span>
                                    {item.subtitle ? (
                                        <span className="text-muted-foreground truncate text-xs">
                                            {item.subtitle}
                                        </span>
                                    ) : null}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    );
                })}

                {isSearching && search.isError ? (
                    <div className="text-muted-foreground px-3 py-2 text-xs">
                        {t(
                            'commandCenter.unavailable',
                            'Search is unavailable — showing commands only.',
                        )}
                    </div>
                ) : null}
            </CommandList>
        </CommandDialog>
    );
}
